import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { Toast } from '@/shared/components/toast/toast';
import { MenuItem, MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';

import { ComissaoCadastroService } from '@/core/api/sicom-api/comissoes/cadastros/comissao-cadastro.service';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { PermissionInput } from '@/core/permissions/permission.model';
import { CctMembroResponse } from '@/core/api/sicom-api/comissoes/cadastros/comissao-cadastro.model';
import { ComissaoReuniaoFiltroRequest, ComissaoReuniaoResponse, EComissaoReuniaoTipoPeriodoFiltro } from '@/core/api/sicom-api/comissoes/reunioes/comissao-reuniao.model';
import { ComissaoReuniaoService } from '@/core/api/sicom-api/comissoes/reunioes/comissao-reuniao.service';
import { ComposicaoMembrosComissoesComponent } from '@/features/comissoes/components/composicao-membros-comissoes/composicao-membros-comissoes';
import { ReuniaoComissao, StatusReuniaoComissao } from '@/features/comissoes/dashboard-comissoes/comissoes-dashboard.model';
import { BotaoAcoes, EInconPos } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { CardContent } from '@/shared/components/card-content/card-content';
import { SendWhatsapp } from '@/shared/components/send-whatsapp/send-whatsapp';

type AcaoPainelComissoes = 'abrir-ata' | 'editar' | 'cancelar' | 'excluir' | 'ir-para-atividades' | 'visualizar-comissao-nucleo' | 'nova-atividade-reuniao' | 'status-ata-anterior' | 'status-ata-proximo' | 'reativar-reuniao' | 'whatsapp';

@Component({
    selector: 'app-painel-comissoes',
    standalone: true,
    imports: [CommonModule, ButtonModule, Toast, ComposicaoMembrosComissoesComponent, BotaoAcoes, CardContent, SendWhatsapp],
    providers: [MessageService],
    templateUrl: './painel-comissoes.html',
    styleUrls: ['./painel-comissoes.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PainelComissoesComponent implements OnInit, OnDestroy {
    protected readonly EInconPos = EInconPos;

    /** Componente de envio de WhatsApp (disparado pela ação "Compartilhar" da reunião atual). */
    readonly sendWhatsappComponent = viewChild<SendWhatsapp>('sendWhatsapp');

    carregando = false;
    carregandoMembros = false;
    reunioes: ReuniaoComissao[] = [];
    reuniaoAtual: ReuniaoComissao | null = null;
    membrosReuniaoAtual: CctMembroResponse[] = [];

    private consultaSub?: Subscription;
    private consultaMembrosSub?: Subscription;

    constructor(
        private readonly router: Router,
        private readonly comissaoReuniaoService: ComissaoReuniaoService,
        private readonly comissaoCadastroService: ComissaoCadastroService,
        private readonly messageService: MessageService,
        private readonly cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.carregarDashboard();
    }

    ngOnDestroy(): void {
        this.consultaSub?.unsubscribe();
        this.consultaMembrosSub?.unsubscribe();
    }

    get proximaReuniao(): ReuniaoComissao | null {
        if (!this.reuniaoAtual) {
            return null;
        }

        const dataAtual = this.reuniaoAtual.dataInicio?.getTime() ?? Number.NEGATIVE_INFINITY;
        const codigoAtual = this.reuniaoAtual.codigo;

        return (
            this.reunioesAgendadasOrdenadas.find((reuniao) => {
                const data = reuniao.dataInicio?.getTime() ?? Number.NEGATIVE_INFINITY;
                return data > dataAtual || (data === dataAtual && reuniao.codigo !== codigoAtual);
            }) ?? null
        );
    }

    get reuniaoAnterior(): ReuniaoComissao | null {
        if (!this.reuniaoAtual) {
            return null;
        }

        const dataAtual = this.reuniaoAtual.dataInicio?.getTime() ?? Number.NEGATIVE_INFINITY;
        const codigoAtual = this.reuniaoAtual.codigo;

        return (
            [...this.reunioesAgendadasOrdenadas].reverse().find((reuniao) => {
                const data = reuniao.dataInicio?.getTime() ?? Number.NEGATIVE_INFINITY;
                return data < dataAtual || (data === dataAtual && reuniao.codigo !== codigoAtual);
            }) ?? null
        );
    }

    private get reunioesAgendadasOrdenadas(): ReuniaoComissao[] {
        return this.reunioes.filter((reuniao) => reuniao.dataInicio && !this.statusFinalizado(reuniao.status)).sort((a, b) => this.compararDatas(a.dataInicio, b.dataInicio));
    }

    private get primeiraReuniaoFutura(): ReuniaoComissao | null {
        const hoje = this.inicioDoDia(new Date()).getTime();
        const futuras = this.reunioesAgendadasOrdenadas.filter((reuniao) => reuniao.dataInicio && reuniao.dataInicio.getTime() >= hoje);
        return futuras[0] ?? this.reunioesAgendadasOrdenadas[0] ?? this.reunioes[0] ?? null;
    }

    carregarDashboard(): void {
        this.carregando = true;

        const request: ComissaoReuniaoFiltroRequest = {
            tipoPeriodo: EComissaoReuniaoTipoPeriodoFiltro.PROXIMOS_30_DIAS,
            excluirStatusReuniaoExcluida: true
        };

        this.consultaSub?.unsubscribe();
        this.consultaSub = this.comissaoReuniaoService.consultarReunioes(request, 0, 50).subscribe({
            next: (reunioes) => {
                this.reunioes = (reunioes.content ?? []).map((item) => this.mapearReuniao(item)).sort((a, b) => this.compararDatas(a.dataInicio, b.dataInicio));
                this.carregando = false;
                const reuniaoPreservada = this.reuniaoAtual ? this.reunioes.find((reuniao) => reuniao.codigo === this.reuniaoAtual?.codigo) : null;
                this.selecionarReuniaoAtual(reuniaoPreservada ?? this.primeiraReuniaoFutura);
                this.cdr.markForCheck();
            },
            error: () => {
                this.reunioes = [];
                this.reuniaoAtual = null;
                this.membrosReuniaoAtual = [];
                this.carregando = false;
                this.carregandoMembros = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Não foi possível carregar o dashboard de comissões.'
                });
                this.cdr.markForCheck();
            }
        });
    }

    carregarMembrosProximaReuniao(): void {
        const idEstrutura = this.reuniaoAtual?.idEstruturaOrganizacional;

        this.consultaMembrosSub?.unsubscribe();

        if (!idEstrutura) {
            this.membrosReuniaoAtual = [];
            this.carregandoMembros = false;
            return;
        }

        this.carregandoMembros = true;
        this.consultaMembrosSub = this.comissaoCadastroService.listarMembrosAtuais(idEstrutura).subscribe({
            next: (membros) => {
                this.membrosReuniaoAtual = membros ?? [];
                this.carregandoMembros = false;
                this.cdr.markForCheck();
            },
            error: () => {
                this.membrosReuniaoAtual = [];
                this.carregandoMembros = false;
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Atenção',
                    detail: 'Não foi possível carregar os membros da comissão.'
                });
                this.cdr.markForCheck();
            }
        });
    }

    abrirAcompanhamento(reuniao: ReuniaoComissao): void {
        this.router.navigate(['/comissoes/acompanhamento'], {
            queryParams: { reuniao: reuniao.codigo }
        });
    }

    abrirAcompanhamentoGeral(): void {
        this.router.navigate(['/comissoes/acompanhamento']);
    }

    irParaProximaReuniao(): void {
        const reuniao = this.proximaReuniao;
        if (reuniao) {
            this.selecionarReuniaoAtual(reuniao);
        }
    }

    irParaReuniaoAnterior(): void {
        const reuniao = this.reuniaoAnterior;
        if (reuniao) {
            this.selecionarReuniaoAtual(reuniao);
        }
    }

    itensAcoesReuniaoAtual(): MenuItem[] {
        const reuniao = this.reuniaoAtual;
        const idStatusReuniao = this.idStatusReuniao(reuniao);
        const possuiReuniao = Boolean(reuniao);
        const permiteAtaEStatus = possuiReuniao && this.statusPermiteAtaEStatus(idStatusReuniao);
        const permiteNovaAtividade = possuiReuniao && this.statusPermiteNovaAtividade(idStatusReuniao);
        const permiteReativar = possuiReuniao && idStatusReuniao === 5;
        const podeExibirStatusAnterior = Boolean(reuniao?.idStatusAnterior && reuniao.idStatusAnterior > 0 && idStatusReuniao !== 3);
        const podeExibirProximoStatus = Boolean(reuniao?.idProximoStatus && reuniao.idProximoStatus > 0 && idStatusReuniao !== 3);

        return [
            this.criarItemAcao('abrir-ata', 'Abrir Ata', 'pi pi-file-edit', 'bg-indigo-500', !permiteAtaEStatus, true, PERMISSOES.comissoes.atas.gerarProtocolo),
            this.criarItemAcao('editar', 'Editar', 'pi pi-pencil', 'bg-blue-500', !possuiReuniao, true, PERMISSOES.comissoes.reunioes.editar),
            this.criarItemAcao('cancelar', 'Cancelar', 'pi pi-ban', 'bg-orange-500', !possuiReuniao, true, PERMISSOES.comissoes.reunioes.cancelar),
            this.criarItemAcao('excluir', 'Excluir', 'pi pi-trash', 'bg-red-500', !possuiReuniao, true, PERMISSOES.comissoes.reunioes.excluir),
            this.criarItemAcao('whatsapp', 'Compartilhar WhatsApp', 'pi pi-whatsapp', 'bg-green-500', !possuiReuniao),
            this.criarItemAcao('ir-para-atividades', 'Ir para Atividades', 'pi pi-arrow-right', 'bg-blue-500', !possuiReuniao, possuiReuniao && reuniao?.atividades === 'Sim'),
            this.criarItemAcao('visualizar-comissao-nucleo', 'Visualizar Comissão / Núcleo', 'pi pi-eye', 'bg-surface-700', !possuiReuniao),
            this.criarItemAcao('nova-atividade-reuniao', 'Nova atividade para esta reunião', 'pi pi-plus-circle', 'bg-surface-500', !permiteNovaAtividade, true, PERMISSOES.comissoes.atividades.criar),
            this.criarItemAcao(
                'status-ata-anterior',
                reuniao?.descricaoStatusAtaAnterior ? `Retornar para: ${reuniao.descricaoStatusAtaAnterior}` : 'Status anterior da ata',
                'pi pi-arrow-left',
                'bg-amber-500',
                !permiteAtaEStatus,
                podeExibirStatusAnterior,
                PERMISSOES.comissoes.atas.alterarStatus
            ),
            this.criarItemAcao(
                'status-ata-proximo',
                reuniao?.descricaoStatusAtaProximo ? `Avançar para: ${reuniao.descricaoStatusAtaProximo}` : 'Próximo status da ata',
                'pi pi-arrow-right',
                'bg-emerald-500',
                !permiteAtaEStatus,
                podeExibirProximoStatus,
                PERMISSOES.comissoes.atas.alterarStatus
            ),
            this.criarItemAcao('reativar-reuniao', 'Reativar Reunião', 'pi pi-refresh', 'bg-surface-500', !permiteReativar, true, PERMISSOES.comissoes.reunioes.reativar)
        ];
    }

    executarAcaoReuniaoAtual(acao: AcaoPainelComissoes): void {
        const reuniao = this.reuniaoAtual;
        if (!reuniao) {
            return;
        }

        if (acao === 'whatsapp') {
            this.sendWhatsappComponent()?.abrirWhatsapp();
            return;
        }

        if (acao === 'ir-para-atividades') {
            this.router.navigate(['/comissoes/atividades'], {
                queryParams: { reuniao: reuniao.codigo }
            });
            return;
        }

        if (acao === 'nova-atividade-reuniao') {
            this.router.navigate(['/comissoes/atividades'], {
                queryParams: { reuniao: reuniao.codigo, novaAtividade: 'true' }
            });
            return;
        }

        if (acao === 'visualizar-comissao-nucleo' && reuniao.idEstruturaOrganizacional) {
            this.router.navigate(['/comissoes/acompanhamento/comissoes'], {
                queryParams: { id: reuniao.idEstruturaOrganizacional }
            });
            return;
        }

        this.router.navigate(['/comissoes/acompanhamento'], {
            queryParams: { reuniao: reuniao.codigo, acao }
        });
    }

    formatarDataHora(data: Date | null): string {
        if (!data) {
            return '-';
        }

        return new Intl.DateTimeFormat('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(data);
    }

    formatarPeriodo(reuniao: ReuniaoComissao): string {
        if (!reuniao.dataInicio || !reuniao.dataTermino) {
            return '-';
        }

        const hora = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });
        return `${hora.format(reuniao.dataInicio)} - ${hora.format(reuniao.dataTermino)}`;
    }

    formatarDistancia(data: Date | null): string {
        if (!data) {
            return 'Data não informada';
        }

        const hoje = this.inicioDoDia(new Date()).getTime();
        const alvo = this.inicioDoDia(data).getTime();
        const dias = Math.round((alvo - hoje) / 86400000);

        if (dias === 0) {
            return 'Hoje';
        }

        if (dias === 1) {
            return 'Amanhã';
        }

        if (dias > 1) {
            return `Em ${dias} dias`;
        }

        return `Há ${Math.abs(dias)} dias`;
    }

    rotuloStatus(status: StatusReuniaoComissao): string {
        const rotulos: Record<StatusReuniaoComissao, string> = {
            agendada: 'Agendada',
            remarcada: 'Remarcada',
            realizada: 'Realizada',
            cancelada: 'Cancelada',
            excluida: 'Excluída'
        };
        return rotulos[status] ?? '-';
    }

    classeStatus(status: StatusReuniaoComissao): string {
        return `status-${status}`;
    }

    rotuloAtividades(reuniao: ReuniaoComissao): string {
        return this.temAtividades(reuniao) ? 'Com atividades' : 'Sem atividades';
    }

    classeAtividades(reuniao: ReuniaoComissao): string {
        return this.temAtividades(reuniao) ? 'atividades-sim' : 'atividades-nao';
    }

    rotuloVisivelPortal(reuniao: ReuniaoComissao): string {
        return reuniao.visivelPortal ? 'Sim' : 'Não';
    }

    classeVisivelPortal(reuniao: ReuniaoComissao): string {
        return reuniao.visivelPortal ? 'portal-sim' : 'portal-nao';
    }

    classeModalidade(reuniao: ReuniaoComissao): string {
        const modalidade = this.normalizarTexto(reuniao.modalidade);
        if (modalidade.includes('PRESENC')) {
            return 'modalidade-presencial';
        }
        if (modalidade.includes('VIRTUAL')) {
            return 'modalidade-virtual';
        }
        if (modalidade.includes('HIBRID')) {
            return 'modalidade-hibrida';
        }
        return 'badge-neutro';
    }

    classeStatusAta(reuniao: ReuniaoComissao): string {
        const status = this.normalizarTexto(reuniao.statusAta);
        if (!status) {
            return 'badge-neutro';
        }
        if (status.includes('APROV') || status.includes('ASSIN') || status.includes('FINAL')) {
            return 'ata-ok';
        }
        if (status.includes('PEND') || status.includes('ABERT') || status.includes('ANDAMENTO') || status.includes('MINUTA')) {
            return 'ata-alerta';
        }
        if (status.includes('CANCEL') || status.includes('EXCL') || status.includes('REJEIT')) {
            return 'ata-erro';
        }
        return 'ata-info';
    }

    resumoConfirmacoes(reuniao: ReuniaoComissao): string {
        if (!reuniao.convocacoes) {
            return 'Sem convocações';
        }

        const percentual = Math.round((reuniao.confirmacoes / reuniao.convocacoes) * 100);
        return `${reuniao.confirmacoes}/${reuniao.convocacoes} confirmadas (${percentual}%)`;
    }

    private mapearReuniao(item: ComissaoReuniaoResponse): ReuniaoComissao {
        const totalMembros = this.numeroSeguro(item.totalMembros);
        const totalConvocacoes = this.numeroSeguro(item.totalConvocacoes);
        const totalConfirmacoes = this.numeroSeguro(item.totalConfirmacoes);

        return {
            codigo: String(item.idReuniao ?? ''),
            tipoPessoa: 'acompanhamento',
            status: this.mapearStatusReuniao(item.dsStatusReuniao),
            idStatusReuniao: item.idStatusReuniao ?? undefined,
            descricaoStatus: item.dsStatusReuniao ?? undefined,
            visivelPortal: this.normalizarTexto(item.inVisivelPortal) === 'S',
            modalidade: this.mapearModalidade(item.inModalidade, item.dsModalidade),
            atividades: item.possuiAtividades && item.possuiAtividades > 0 ? 'Sim' : 'Não',
            codigoComissao: String(item.idEstruturaOrganizacional ?? '-'),
            idEstruturaOrganizacional: item.idEstruturaOrganizacional ?? undefined,
            nomeComissao: item.dsEstruturaOrganizacional || '-',
            statusAta: item.dsStatusAta || '',
            idStatusAta: item.idStatusAta ?? undefined,
            descricaoStatusAta: item.dsStatusAta ?? undefined,
            idProximoStatus: item.idProximoStatus ?? undefined,
            idStatusAnterior: item.idStatusAnterior ?? undefined,
            descricaoStatusAtaProximo: item.dsStatusAtaProximo ?? undefined,
            descricaoStatusAtaAnterior: item.dsStatusAtaAnterior ?? undefined,
            local: item.dsLocalReuniao || '-',
            idLocalReuniao: item.idLocalReuniao ?? undefined,
            dataHoraCadastro: this.converterData(item.dtUltimaAlteracao),
            dataInicio: this.converterData(item.dhInicioReuniao),
            dataTermino: this.converterData(item.dhFimReuniao),
            convocacoes: totalConvocacoes,
            confirmacoes: totalConfirmacoes,
            totalMembros,
            atosConcessao: this.numeroSeguro(item.totalAtos),
            importante: item.inImportante || '',
            dsImportante: item.dsImportante ?? undefined,
            protocolo: item.numeroProtocolo || '',
            assistente: item.nmSolicitanteAssistente || '-',
            idSolicitanteAssistente: item.idSolicitanteAssistente ?? undefined,
            ufSolicitanteAssistente: item.ufSolicitanteAssistente ?? undefined,
            idSolicitanteCoordenador: item.idSolicitanteCoordenador ?? undefined,
            ufSolicitanteCoordenador: item.sgUfCoordenador ?? undefined,
            nomeCoordenador: item.nmSolicitanteCoordenador ?? undefined,
            membrosConvidadosPresentes: [],
            tipoComissao: 'comissao-interna',
            idTipoComissaoCt: item.idTipoComissaoCt ?? undefined,
            possuiConvocacaoPendente: totalMembros > 0 && totalConvocacoes < totalMembros,
            possuiConfirmacaoPendente: totalConvocacoes > 0 && totalConfirmacoes < totalConvocacoes,
            ataMarcada: Boolean(item.dsStatusAta)
        };
    }

    private criarItemAcao(acao: AcaoPainelComissoes, label: string, icon: string, styleClass: string, disabled: boolean, visible: boolean = true, permission?: PermissionInput): MenuItem {
        return {
            id: acao,
            label,
            icon,
            styleClass,
            disabled,
            visible,
            permission,
            command: () => this.executarAcaoReuniaoAtual(acao)
        };
    }

    private idStatusReuniao(reuniao: ReuniaoComissao | null): number | null {
        if (!reuniao) {
            return null;
        }

        if (typeof reuniao.idStatusReuniao === 'number' && Number.isFinite(reuniao.idStatusReuniao)) {
            return reuniao.idStatusReuniao;
        }

        const mapa: Record<StatusReuniaoComissao, number> = {
            agendada: 1,
            remarcada: 2,
            cancelada: 3,
            realizada: 4,
            excluida: 5
        };
        return mapa[reuniao.status] ?? null;
    }

    private statusPermiteAtaEStatus(idStatusReuniao: number | null): boolean {
        return idStatusReuniao === 1 || idStatusReuniao === 2 || idStatusReuniao === 4;
    }

    private statusPermiteNovaAtividade(idStatusReuniao: number | null): boolean {
        return idStatusReuniao === 1 || idStatusReuniao === 2;
    }

    private selecionarReuniaoAtual(reuniao: ReuniaoComissao | null): void {
        this.reuniaoAtual = reuniao;
        this.carregarMembrosProximaReuniao();
    }

    private mapearStatusReuniao(status?: string | null): StatusReuniaoComissao {
        const valor = this.normalizarTexto(status);

        if (valor.includes('REMARC')) {
            return 'remarcada';
        }

        if (valor.includes('REALIZ')) {
            return 'realizada';
        }

        if (valor.includes('CANCEL')) {
            return 'cancelada';
        }

        if (valor.includes('EXCL')) {
            return 'excluida';
        }

        return 'agendada';
    }

    private mapearModalidade(modalidade?: string | null, descricao?: string | null): string {
        if (descricao) {
            return descricao;
        }

        const valor = this.normalizarTexto(modalidade);

        if (valor === 'V') {
            return 'Virtual';
        }

        if (valor === 'H') {
            return 'Híbrida';
        }

        if (valor === 'P') {
            return 'Presencial';
        }

        return 'Não informada';
    }

    private temAtividades(reuniao: ReuniaoComissao): boolean {
        const valor = (reuniao.atividades ?? '').trim().toLowerCase();
        return Boolean(valor && valor !== 'nao' && valor !== 'não' && valor !== 'n' && valor !== '-' && valor !== '0');
    }

    private statusFinalizado(status: StatusReuniaoComissao): boolean {
        return status === 'realizada' || status === 'cancelada' || status === 'excluida';
    }

    private compararDatas(a: Date | null, b: Date | null): number {
        return (a?.getTime() ?? Number.MAX_SAFE_INTEGER) - (b?.getTime() ?? Number.MAX_SAFE_INTEGER);
    }

    private converterData(valor?: string | null): Date | null {
        if (!valor) {
            return null;
        }

        const data = new Date(valor);
        return Number.isNaN(data.getTime()) ? null : data;
    }

    private inicioDoDia(data: Date): Date {
        const normalizada = new Date(data);
        normalizada.setHours(0, 0, 0, 0);
        return normalizada;
    }

    private normalizarTexto(valor?: string | null): string {
        return (valor ?? '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .toUpperCase();
    }

    gerarMensagemWhatsappReuniao(reuniao: ReuniaoComissao): string {
        if (!reuniao) {
            return '';
        }

        const dataStr = this.formatarDataHora(reuniao.dataInicio);
        const statusStr = reuniao.descricaoStatusAta || reuniao.statusAta;

        const linhas = [
            '\u{1F465} *Reunião de comissão*',
            reuniao.nomeComissao ? `\u{1F3DB}\uFE0F Comissão/CT: ${reuniao.nomeComissao}` : '',
            reuniao.codigo ? `\u{1F522} Reunião: #${reuniao.codigo}` : '',
            dataStr ? `\u{1F4C5} Data/Hora: ${dataStr}` : '',
            reuniao.local ? `\u{1F4CD} Local: ${reuniao.local}` : '',
            statusStr ? `\u{1F4CB} Status da Ata: ${statusStr}` : ''
        ];

        return linhas.filter((linha) => linha !== '').join('\n');
    }

    private numeroSeguro(valor?: number | null): number {
        return Number.isFinite(valor) ? Number(valor) : 0;
    }
}
